/** @format */
import { ButtonSize } from '@mpb-com/feather/components/atoms/button';
import { PillLabel } from '@mpb-com/feather/components/atoms/pill-label';
import { Thumbnail } from '@mpb-com/feather/components/atoms/thumbnail';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import React, { FunctionComponent, useContext } from 'react';

import { CloudflareImageSize } from '@toucan/client/global/cloudflare';
import { formatAsCurrency } from '@toucan/client/global/currency';
import { MarketContext } from '@toucan/client/global/market';
import { WrappedLink } from '@toucan/client/global/nextjs';
import { fallbackImagePath } from '@toucan/client/global/product';

import { FormattedCondition } from '@toucan/client/shared/components/formatted-condition';
import { LinkVariant } from '@toucan/client/shared/components/link';
import { useProductUrl } from '@toucan/client/shared/hooks/use-product-url';

import { CloudflareImage } from '@toucan/client/blocks/components/cloudflare-image';
import { ProductAddToBasket } from '@toucan/client/blocks/components/product-add-to-basket';

import { DEFAULT_TEST_ID } from './constants';
import { ProductComparisonItemInStockHeader as StyledProductComparisonItemInStockHeader } from './in-stock-header.styles';
import { ProductComparisonItemInStockProps } from './types';

export const ProductComparisonItemInStock: FunctionComponent<
    ProductComparisonItemInStockProps
> = ({
    columnIndex,
    productData,
    modelUrlSegment,
    'data-testid': dataTestId = DEFAULT_TEST_ID,
}) => {
    const { name, condition, images, value, sku } = productData;

    const { t } = useTranslation();
    const { activeMarket } = useContext(MarketContext);

    const { href, templatePath } = useProductUrl(sku, modelUrlSegment);
    const { query = {} } = useRouter();

    const formatPrice = formatAsCurrency(
        activeMarket.iso,
        activeMarket.currency
    );

    const isCurrentProduct = sku === query.sku;
    const listPrice = formatPrice(value / 100, { trailingZeros: false });

    return (
        <StyledProductComparisonItemInStockHeader
            data-testid={dataTestId}
            $columnIndex={columnIndex}
            $rowIndex={0}
        >
            <WrappedLink
                href={templatePath}
                as={href}
                passHref
                variant={LinkVariant.CUSTOM}
                target="_blank"
            >
                <Thumbnail
                    data-testid={`${dataTestId}__thumbnail`}
                    className="product-comparison-item-image"
                >
                    <CloudflareImage
                        src={images[0]?.imageUrl}
                        fallbackSrc={fallbackImagePath}
                        alt={name}
                        imageSize={CloudflareImageSize.THUMBNAIL}
                    />
                </Thumbnail>
            </WrappedLink>
            <div className="product-comparison-item-details">
                {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
                {isCurrentProduct && (
                    <PillLabel className="product-comparison-item-pill">
                        {t('This product')}
                    </PillLabel>
                )}
                <span className="product-comparison-item-price">
                    {listPrice}
                </span>
                <FormattedCondition
                    className="product-comparison-item-condition"
                    condition={condition}
                    boldConditionValue
                />
            </div>
            <ProductAddToBasket
                className="product-comparison-item-button"
                productData={productData}
                size={ButtonSize.SMALL}
            />
        </StyledProductComparisonItemInStockHeader>
    );
};
